import { Icon, IconButton, Tooltip } from '@kontentino/ui';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faFilm } from '@fortawesome/pro-regular-svg-icons/faFilm';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons/faCloudArrowUp';
import { faArrowDownToLine } from '@fortawesome/pro-regular-svg-icons/faArrowDownToLine';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';
import { ComponentProps, FC } from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Logger from 'utils/logger';

const ACTION_TYPES = {
  edit: {
    icon: faPen,
    label: 'edit',
  },
  customThumbnail: {
    icon: faFilm,
    label: 'customThumbnail.label',
  },
  delete: {
    icon: faTrash,
    label: 'delete',
  },
  download: {
    icon: faArrowDownToLine,
    label: 'download',
  },
  preview: {
    icon: faEye,
    label: 'preview',
  },
  replace: {
    icon: faCloudArrowUp,
    label: 'replace',
  },
  remove: {
    icon: faXmark,
    label: 'remove',
  },
} as const;

export type MediaPreviewActionButtonProps = (
  | {
      actionType: keyof typeof ACTION_TYPES;
      customIcon?: never;
      customTooltip?: never;
      iconSize?: never;
    }
  | {
      actionType: 'custom';
      customIcon: IconDefinition;
      customTooltip: string;
      iconSize?: 'sm' | 'md' | 'lg' | 'xl';
    }
) &
  ComponentProps<typeof IconButton>;

export const MediaPreviewActionButton: FC<MediaPreviewActionButtonProps> = ({
  actionType,
  customIcon,
  customTooltip,
  iconSize = 'sm',
  ...props
}) => {
  const { t } = useTranslation();

  if (actionType === 'custom') {
    if (!customIcon || !customTooltip) {
      Logger.error('Custom action requires customIcon and customTooltip props');
      return null;
    }

    return (
      <Tooltip content={customTooltip}>
        <IconButton
          icon={<Icon icon={customIcon} size={iconSize} />}
          size="medium"
          variant="secondary"
          className="tw-rounded-none"
          {...props}
        />
      </Tooltip>
    );
  }

  const actionConfig = ACTION_TYPES[actionType];

  if (!actionConfig) {
    Logger.error(`Unknown action type: ${actionType}`);
    return null;
  }

  const { icon, label } = actionConfig;

  return (
    <Tooltip content={t(label)}>
      <IconButton
        icon={<Icon icon={icon} />}
        size="small"
        variant="secondary"
        className="tw-rounded-none"
        {...props}
      />
    </Tooltip>
  );
};
